/**
 * Block-level error surfacing for the SWR refresh model (review #56 —
 * finding #2). FAIL LOUD: a block whose fetch failed must NEVER look
 * like a block with current data.
 *
 * Two faces, chosen by whether there is stale content to keep:
 *
 *   - `BlockErrorCard` — the block has NOTHING to show (its first
 *     fetch for this report failed): a compact error card takes the
 *     block's slot in the layout, with the raw error message and a
 *     Retry button that relaunches ONLY that block's fetch.
 *
 *   - `BlockStale` — the block HAS previous data on screen and a
 *     refresh failed: the stale content stays visible (SWR rule) but
 *     gets an unmissable marker — a danger ring + a top-right badge
 *     ("Update failed") whose tooltip carries the raw error and whose
 *     click retries. Stale data is never presented as current.
 *
 * i18n keys (register in BOTH apps' dicts, EN + ES):
 *   dashboard.blockError.title  — "Couldn't load this data"
 *   dashboard.blockError.retry  — "Retry"
 *   dashboard.blockError.stale  — "Update failed — showing previous data"
 */
import type { ReactNode } from "react";
import { AlertTriangle, RotateCw } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface BlockErrorCardProps {
  /** Raw error message from the failed fetch — shown, not hidden. */
  message: string;
  /** What failed, when it is not loading data (a save, a delete). */
  title?: string;
  /** Relaunch ONLY this block's fetch. Absent → no button. */
  onRetry?: () => void;
  /** Uniform-grid mode: stretch to the grid cell. */
  fill?: boolean;
}

/** Compact in-slot error card for a block with no data to keep. */
export function BlockErrorCard({
  message,
  title,
  onRetry,
  fill = false,
}: BlockErrorCardProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      data-pm-block-error-card
      className={`rounded-xl border border-red-200 bg-red-50/50 shadow-sm px-5 py-4 flex flex-col items-start justify-center gap-2${
        fill ? " h-full overflow-hidden" : ""
      }`}
    >
      <div className="flex items-center gap-2 text-negative">
        <AlertTriangle size={15} className="shrink-0" />
        <span className="text-sm font-semibold">
          {title ??
            t("dashboard.blockError.title", {
              defaultValue: "Couldn't load this data",
            })}
        </span>
      </div>
      <p className="text-xs text-negative break-words max-w-full">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-negative hover:bg-red-50 transition-colors"
        >
          <RotateCw size={12} />
          {t("dashboard.blockError.retry", { defaultValue: "Retry" })}
        </button>
      )}
    </div>
  );
}

export interface BlockStaleProps {
  /** Raw error message of the failed refresh; null = healthy (children
   *  render untouched inside the stable wrapper). */
  error: string | null;
  /** Relaunch ONLY this block's fetch. */
  onRetry?: () => void;
  className?: string;
  children: ReactNode;
}

/**
 * Stale-data marker wrapper. Renders a stable `relative` div (so the
 * `h-full` chain of grid-stretched cards survives) and, when `error`
 * is set, overlays the danger ring + retry badge on the kept content.
 */
export function BlockStale({
  error,
  onRetry,
  className,
  children,
}: BlockStaleProps): JSX.Element {
  const { t } = useTranslation();
  const stale = error != null;
  const staleLabel = t("dashboard.blockError.stale", {
    defaultValue: "Update failed — showing previous data",
  });
  return (
    <div
      className={`relative ${className ?? ""}${
        stale ? " rounded-xl ring-2 ring-red-300" : ""
      }`}
      data-pm-block-stale={stale || undefined}
    >
      {children}
      {stale && (
        <button
          type="button"
          onClick={onRetry}
          title={`${staleLabel}: ${error}`}
          className="absolute right-2 top-2 z-20 inline-flex items-center gap-1 rounded-full border border-red-300 bg-red-50 px-2 py-0.5 text-2xs font-semibold text-negative shadow-sm hover:bg-red-100 transition-colors"
        >
          <AlertTriangle size={11} className="shrink-0" />
          <span className="max-w-[160px] truncate">{staleLabel}</span>
          {onRetry && <RotateCw size={11} className="shrink-0" />}
        </button>
      )}
    </div>
  );
}
